/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { API, CONFIGUREROUTES, DIALOGOPENFOR, LOCAL_STORAGE } from "../../../globals/globals";

import { lanDeviceInterfaceColumn } from "../../../components/Shared/Table/Columns";
import { useLocation } from "react-router-dom";
import { useStoreDispatch } from "../../../store/hooks";
import { setConfigCounts, setLANDeviceIndexes, setLanDeviceList, setConfigStatusData } from "../../../store/api/deviceConfig/reducers";
import { useGetConfigStatusQuery, useGetLanDevicesQuery, useSetDeviceConfigMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { useSelector } from "react-redux";
import { lanDeviceListSelector, selectedIPInterfaceSelector, selectedLanDeviceSelector } from "../../../store/api/deviceConfig/selectors";
import { ILanDevice } from "../../../store/api/deviceConfig/types";
import { setOpenToast } from "../../../store/helpers/helperReducers";
import { OPERATIONTYPES, constStr, xsdPathStr } from "../../../globals/constants";
import { prepareIPInterfacePayload, prepareLANDevicePayload } from "../../../components/Configuration/helpers";
import { confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { errorMessage } from "../../../utils/helpers";
import { selectProfilePermissionsSelector } from "../../../store/api/auth/selectors";
import ProfilePermissions from "../../../utils/ProfilePermissions";

export const useDisplayIPInterfaceData = () => {
  const dispatch = useStoreDispatch();
  const location = useLocation();
  const locationPath = location?.pathname.split("/");

  const [pageState, setPageState] = useState(0);
  const [rowCount, setRowCount] = useState(10);
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
  const args = { filters: `page=${pageState.toString()}&size=${rowCount.toString()}`, deviceKey: locationPath[2] };
  const { data, isLoading, refetch } = useGetLanDevicesQuery(args, {
    pollingInterval: API.ENDPOINTS.IPINTERFACE.pollingInterval || API.POLLING_INTERVAL,
  });

  const [setDeviceConfig, configStatus] = useSetDeviceConfigMutation();

  const uiseId = localStorage.getItem(LOCAL_STORAGE.USER);
  const args1 = {
    userId: !!uiseId ? uiseId : "",
    tableId: xsdPathStr.lanDevice.tableId,
    deviceObjectKey: locationPath[2], //"22056S52746" //locationPath[2],
  };

  const configStatusData = useGetConfigStatusQuery(args1, {
    pollingInterval: API.ENDPOINTS.CONFIGSTATUS.pollingInterval,
  });

  const lanDeviceList = useSelector(lanDeviceListSelector) as ILanDevice[];
  const selectedLanDevice = useSelector(selectedLanDeviceSelector);
  const selectedIPInterface = useSelector(selectedIPInterfaceSelector);
  const selectedDevice = useSelector(selectedDeviceSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const lanDeviceIndexes =
    lanDeviceList.length > 0 &&
    (lanDeviceList.map((item: ILanDevice) => Object.keys(item).map((keyitem) => keyitem === "index") && item.index) as string[]);

  useEffect(() => {
    if (!!data?.data && data?.data.length > 0) {
      dispatch(setLanDeviceList(data?.data));
    }
  }, [data?.data, data?.totalItems, dispatch]);

  useEffect(() => {
    if (!!lanDeviceIndexes && lanDeviceIndexes?.length > 0) {
      dispatch(setLANDeviceIndexes(lanDeviceIndexes));
    }
  }, [dispatch, lanDeviceIndexes]);

  useEffect(() => {
    if (!!configStatusData.data && configStatusData.data?.length > 0) {
      dispatch(setConfigStatusData(configStatusData.data));
    }
  }, [dispatch, configStatusData.data]);

  const deleteLANDeviceHandler = async (actionName: string) => {
    if (!actionName) return;
    try {
      let response: any;
      let messageStr: string = "";
      if (actionName.match(constStr.yesDelete.toLowerCase())) {
        if (openConfirmDialog === DIALOGOPENFOR.LANDEVICE) {
          const lanDevicePayload =
            !!selectedDevice?.objectKey && prepareLANDevicePayload(undefined, selectedLanDevice, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(lanDevicePayload);
          messageStr = `The deletion process for ${selectedLanDevice?.name} has been initiated.`;
        } else if (openConfirmDialog === DIALOGOPENFOR.IPINTERFACE) {
          let oldIPinterface;
          if (selectedIPInterface?.vlanId === "0") {
            oldIPinterface = { ...selectedIPInterface, vlanId: selectedLanDevice.index };
          } else {
            oldIPinterface = selectedIPInterface;
          }
          const ipInterfacePayload =
            !!selectedDevice?.objectKey && prepareIPInterfacePayload(undefined, oldIPinterface, selectedDevice?.objectKey, OPERATIONTYPES.DELETE);
          response = await setDeviceConfig(ipInterfacePayload);
          messageStr = `The deletion process for IPInterface.${selectedIPInterface?.index} of ${selectedLanDevice?.name} has been initiated.`;
        }

        if (response?.error) throw response.error;
        if (response?.data?.status === 200 || response?.data?.statusCodeValue === 200 || response?.data?.statusCode === "OK") {
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: messageStr,
            })
          );
          refetch();
        }
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    permissions,
    data: lanDeviceList,
    isLoading,
    lanDeviceInterfaceColumn,
    setPageState,
    setRowCount,
    rowCount,
    selectedLanDevice,
    selectedIPInterface,
    deleteLANDeviceHandler,
    configStatusData,
  };
};
